// @ts-check

/**
 * Manages the full screen lightbox for viewing gallery images.
 * Supports prev/next navigation within the same hall.
 */
export class Lightbox {
  /**
   * @param {HTMLElement} container
   * @param {Object} [options={}]
   * @param {() => void} [options.onOpen]
   * @param {() => void} [options.onClose]
   */
  constructor(container, options = {}) {
    this.container = container;
    this.image = /** @type {HTMLImageElement} */ (document.getElementById('lightbox-image'));
    this.caption = /** @type {HTMLElement} */ (document.getElementById('lightbox-caption'));
    this.closeButton = /** @type {HTMLButtonElement} */ (document.getElementById('lightbox-close'));
    this.prevButton = /** @type {HTMLButtonElement|null} */ (document.getElementById('lightbox-prev'));
    this.nextButton = /** @type {HTMLButtonElement|null} */ (document.getElementById('lightbox-next'));

    this.isOpen = false;
    /** @type {HTMLElement|null} */
    this.lastFocused = null;
    /** Whether this instance owns the body scroll lock (avoids clobbering the nav menu). */
    this.ownsScrollLock = false;
    /** @type {ReturnType<typeof setTimeout>|null} — pending close cleanup timer */
    this._closeTimer = null;
    /** Current works array for prev/next navigation */
    this._works = [];
    /** Current index in the works array */
    this._currentIndex = -1;
    /** Callbacks for external scroll lock coordination (e.g., Lenis stop/start) */
    this._onOpen = options.onOpen ?? (() => {});
    this._onClose = options.onClose ?? (() => {});
    this.bindEvents();
  }

  /**
   * Attaches close handlers.
   */
  bindEvents() {
    this.closeButton?.addEventListener('click', () => this.close());

    this.prevButton?.addEventListener('click', () => this.prev());
    this.nextButton?.addEventListener('click', () => this.next());

    this.container.addEventListener('click', (event) => {
      if (event.target === this.container) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'Tab') {
        // Trap focus inside the dialog so keyboard users can't tab into the
        // page behind the overlay.
        this.trapFocus(event);
      }
    });

    window.addEventListener('gallery:openLightbox', (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      if (detail) {
        // detail.work is the full work object; detail.works is the hall array;
        // detail.index is the position within that array.
        this.open(detail.work, detail.works, detail.index);
      }
    });
  }

  /**
   * Confines Tab focus to the lightbox controls while open.
   * @param {KeyboardEvent} event
   */
  trapFocus(event) {
    const focusable = this.container.querySelectorAll(
      'button, [href], input, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = /** @type {HTMLElement} */ (focusable[0]);
    const last = /** @type {HTMLElement} */ (focusable[focusable.length - 1]);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Opens the lightbox for a work item.
   * @param {Object} work
   * @param {string} work.src
   * @param {string} work.title
   * @param {string} work.alt
   * @param {Object[]} [works=[]] — full hall works array for prev/next
   * @param {number} [index=0] — position within works array
   */
  open(work, works = [], index = 0) {
    if (!this.image || !this.caption) return;

    // Cancel any pending close-cleanup timer so a rapid reopen doesn't get
    // the image/caption wiped by the old setTimeout (B1 race condition).
    if (this._closeTimer !== null) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }

    this._works = works;
    this._currentIndex = index;

    this.image.src = `${import.meta.env.BASE_URL}${work.src.startsWith('/') ? work.src.slice(1) : work.src}`;
    this.image.alt = work.alt;
    this.caption.textContent = work.title;
    // Entrance: the image sits hidden and slightly small until its own decode
    // finishes, then settles in. Without this it pops in at full size over the
    // fading overlay and the open reads as a hard cut.
    this.image.classList.remove('is-shown');
    this._revealImage();

    this.updateNavButtons();

    this.container.hidden = false;
    requestAnimationFrame(() => {
      this.container.classList.add('is-open');
    });

    // Only lock body scroll if nothing else (e.g. the nav menu) already has.
    if (document.body.style.overflow !== 'hidden') {
      document.body.style.overflow = 'hidden';
      this.ownsScrollLock = true;
    }

    // External scroll coordination (e.g., Lenis stop).
    this._onOpen();

    // Move focus into the dialog for keyboard/AT users; restore on close.
    this.lastFocused = /** @type {HTMLElement} */ (document.activeElement);
    this.closeButton?.focus();
    this.isOpen = true;
  }

  /**
   * Reveals the lightbox image once it is actually decodable, so the
   * settle-in transition plays against a visible picture rather than an
   * empty frame. Runs on open and on prev/next navigation.
   */
  _revealImage() {
    if (!this.image) return;
    const reveal = () => this.image.classList.add('is-shown');
    if (this.image.complete && this.image.naturalWidth > 0) {
      requestAnimationFrame(reveal);
      return;
    }
    // `once` handlers: a failed load must also reveal, or the lightbox would
    // stay permanently blank instead of showing alt text.
    this.image.addEventListener('load', reveal, { once: true });
    this.image.addEventListener('error', reveal, { once: true });
  }

  /**
   * Closes the lightbox.
   */
  close() {
    if (!this.isOpen) return;

    this.container.classList.remove('is-open');
    if (this.ownsScrollLock) {
      document.body.style.overflow = '';
      this.ownsScrollLock = false;
    }
    // External scroll coordination (e.g., Lenis start).
    this._onClose();
    this.isOpen = false;

    // Restore focus to the element that opened the lightbox.
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
    this.lastFocused = null;

    this._closeTimer = setTimeout(() => {
      this._closeTimer = null;
      this.container.hidden = true;
      if (this.image) {
        this.image.src = '';
        this.image.alt = '';
      }
      if (this.caption) {
        this.caption.textContent = '';
      }
    }, 300);
  }

  /**
   * Navigates to the previous work in the current hall.
   */
  prev() {
    if (this._works.length === 0 || this._currentIndex <= 0) return;
    this._currentIndex -= 1;
    this.showCurrentWork();
  }

  /**
   * Navigates to the next work in the current hall.
   */
  next() {
    if (this._works.length === 0 || this._currentIndex >= this._works.length - 1) return;
    this._currentIndex += 1;
    this.showCurrentWork();
  }

  /**
   * Updates the lightbox to show the current work (by _currentIndex).
   */
  showCurrentWork() {
    const work = this._works[this._currentIndex];
    if (!work || !this.image || !this.caption) return;

    this.image.src = `${import.meta.env.BASE_URL}${work.src.startsWith('/') ? work.src.slice(1) : work.src}`;
    this.image.alt = work.titleZh;
    this.caption.textContent = `${work.titleZh} · ${work.titleEn}`;
    // Navigation gets the same settle-in as the first open, so switching
    // works has a middle state instead of an instant swap.
    this.image.classList.remove('is-shown');
    this._revealImage();
    this.updateNavButtons();
  }

  /**
   * Shows/hides prev/next buttons based on current position.
   */
  updateNavButtons() {
    if (this.prevButton) {
      this.prevButton.hidden = this._currentIndex <= 0;
    }
    if (this.nextButton) {
      this.nextButton.hidden = this._currentIndex >= this._works.length - 1;
    }
  }

  /**
   * Removes all event listeners and clears pending timers.
   */
  destroy() {
    if (this._closeTimer !== null) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
    // Note: keydown and gallery:openLightbox listeners are on document/window
    // and will be garbage-collected when the page unloads. For a SPA we would
    // store references and remove them; for this static site pagehide suffices.
  }
}
